// Org-scoped, size-bounded LRU for unwrapped DEK handles (ADR-0007 / M7).
//
// The hot path (outbound signing, inbound verification) needs a usable DEK per event.
// Unwrapping through the KMS seam on every event is both slow and chatty, so we cache the
// resulting *non-extractable* `CryptoKey` handle in isolate memory. Two rules from the ADR
// shape this:
//
//   1. We cache handles, never raw key bytes — a heap dump yields opaque handles (M7).
//   2. The cache is process-local, size-bounded, evicted under bound, never persisted, and
//      strictly org-scoped so one tenant's handle can never serve another (confused-deputy).
//
// BAA tenants default to a tighter or *zero* cache (unwrap-per-use). That policy is injected
// as `isCacheDisabled(orgId)`: when it returns true the cache is fully bypassed — the loader
// runs every time and nothing is stored for that org.

/** A loader produces a fresh unwrapped DEK handle (typically `KmsProvider.unwrapDek`). */
export type DekLoader = () => Promise<CryptoKey>;

export interface OrgScopedDekCacheOptions {
  /** Hard upper bound on resident handles across all orgs. Must be >= 1. */
  readonly maxEntries: number;
  /**
   * BAA "zero cache" policy hook. Return true for orgs that must unwrap-per-use; those orgs
   * bypass the cache entirely (no read, no store). Defaults to "everything is cacheable".
   */
  readonly isCacheDisabled?: (orgId: string) => boolean;
}

// Cache-key separator. The components (orgId, wrapRef) are opaque internal ids, never
// free-form user text, so a single delimiter is unambiguous. Length-prefixing would harden
// this further if the components ever became attacker-controlled.
const KEY_SEPARATOR = " ";

/**
 * Size-bounded LRU keyed per (orgId, wrapRef). Eviction is global (across orgs) at
 * `maxEntries` — the cache bounds total isolate memory, not per-tenant memory. LRU order is
 * maintained via `Map` insertion order: a hit deletes and re-inserts the entry to move it to
 * the most-recently-used (last) position; the first key is least-recently-used.
 */
export class OrgScopedDekCache {
  readonly #maxEntries: number;
  readonly #isCacheDisabled: (orgId: string) => boolean;
  // Insertion order == recency order; first key is least-recently-used.
  readonly #entries = new Map<string, CryptoKey>();
  // In-flight loads, coalesced so concurrent reads of the same key load once.
  readonly #inflight = new Map<string, Promise<CryptoKey>>();

  constructor(options: OrgScopedDekCacheOptions) {
    if (!Number.isInteger(options.maxEntries) || options.maxEntries < 1) {
      throw new Error("OrgScopedDekCache: maxEntries must be a positive integer");
    }
    this.#maxEntries = options.maxEntries;
    this.#isCacheDisabled = options.isCacheDisabled ?? (() => false);
  }

  /** Resident handle count across all orgs. */
  get size(): number {
    return this.#entries.size;
  }

  /**
   * Return the cached handle for (orgId, wrapRef), or run `load` and cache its result. For a
   * BAA org (cache disabled) the loader always runs and nothing is stored. Concurrent calls
   * for the same key are coalesced into a single loader invocation; a rejected load caches
   * nothing.
   */
  async getOrLoad(orgId: string, wrapRef: string, load: DekLoader): Promise<CryptoKey> {
    if (this.#isCacheDisabled(orgId)) {
      return load();
    }

    const key = `${orgId}${KEY_SEPARATOR}${wrapRef}`;

    const cached = this.#entries.get(key);
    if (cached !== undefined) {
      // Touch: move to most-recently-used position.
      this.#entries.delete(key);
      this.#entries.set(key, cached);
      return cached;
    }

    const pending = this.#inflight.get(key);
    if (pending !== undefined) {
      return pending;
    }

    const promise = (async () => {
      const handle = await load();
      this.#store(key, handle);
      return handle;
    })().finally(() => {
      this.#inflight.delete(key);
    });

    this.#inflight.set(key, promise);
    return promise;
  }

  /** Drop every cached handle for one org (e.g. on key rotation or tenant offboarding). */
  invalidateOrg(orgId: string): void {
    const prefix = `${orgId}${KEY_SEPARATOR}`;
    for (const key of this.#entries.keys()) {
      if (key.startsWith(prefix)) {
        this.#entries.delete(key);
      }
    }
  }

  /** Drop all cached handles. */
  clear(): void {
    this.#entries.clear();
  }

  #store(key: string, handle: CryptoKey): void {
    // A concurrent store for the same key may have landed first; overwrite-in-place keeps the
    // size accounting honest and refreshes recency.
    this.#entries.delete(key);
    this.#entries.set(key, handle);
    while (this.#entries.size > this.#maxEntries) {
      const lru = this.#entries.keys().next().value;
      if (lru === undefined) break;
      this.#entries.delete(lru);
    }
  }
}
